import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  FaCalendarAlt,
  FaClock,
  FaArrowLeft,
  FaCheck,
  FaLaptopMedical,
  FaUserMd,
  FaVideo,
  FaInfoCircle,
  FaCalendarTimes,
  FaExclamationTriangle,
} from "react-icons/fa";
import { AppDispatch, RootState } from "../../api/store";
import {
  doctorDateTimeClearSuccess,
  getDoctorDateTime,
} from "../../api/slices/DoctorDateTimeSlice";

interface DateTimeSelectorProps {
  doctorId: number;
  bookingType: number;
  onBack: () => void;
  onSelectionComplete: (
    date: string,
    time: string,
    timeSlotId: number,
    bookingType: number
  ) => void;
  isNewPatient: boolean;
}

const DateTimeSelection: React.FC<DateTimeSelectorProps> = ({
  doctorId,
  bookingType,
  onBack,
  onSelectionComplete,
  isNewPatient,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { doctorDateTime, doctorDateTimeError, doctorDateTimeLoading } =
    useSelector((state: RootState) => state.doctorDateTime);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"online" | "physical">("physical");
  const [selectedBookingType, setSelectedBookingType] =
    useState<number>(bookingType);
  const [selectedTimeSlotId, setSelectedTimeSlotId] = useState<number | null>(
    null
  );
  const timeSlotsRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(
      getDoctorDateTime({ doctor: doctorId, bookingType: selectedBookingType })
    );
    return () => {
      dispatch(doctorDateTimeClearSuccess());
    };
  }, [dispatch, doctorId, selectedBookingType]);

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setTimeout(() => {
      timeSlotsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, 100);
  };

  const handleTimeSelect = (time: string, timeSlotId: number) => {
    setSelectedTime(time);
    setSelectedTimeSlotId(timeSlotId);
    setTimeout(() => {
      confirmButtonRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, 100);
  };

  const handleConfirm = () => {
    if (selectedDate && selectedTime && selectedTimeSlotId) {
      onSelectionComplete(
        selectedDate,
        selectedTime,
        selectedTimeSlotId,
        selectedBookingType
      );
    }
  };

  const handleTabChange = (tab: "online" | "physical") => {
    setActiveTab(tab);
    setSelectedBookingType(tab === "online" ? 2 : 1);
    setSelectedDate(null);
    setSelectedTime(null);
    // Scroll to top when tab changes
    containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (doctorDateTimeLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="max-w-4xl mx-auto p-4 md:p-6 bg-white rounded-xl shadow-lg"
      >
        {/* Loading Header */}
        <div className="flex items-center mb-6">
          <motion.div
            animate={{ x: [-5, 5, -5] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <FaArrowLeft className="text-gray-300 text-xl mr-4" />
          </motion.div>
          <motion.div
            className="h-8 w-48 bg-gray-200 rounded-lg"
            animate={{
              opacity: [0.6, 1, 0.6],
            }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          />
        </div>

        {/* Loading Tabs */}
        <div className="flex mb-6">
          <motion.div
            className="h-12 w-32 bg-gray-100 rounded-t-lg mr-2"
            animate={{
              opacity: [0.7, 1, 0.7],
            }}
            transition={{ repeat: Infinity, duration: 1.8 }}
          />
          <motion.div
            className="h-12 w-32 bg-gray-100 rounded-t-lg"
            animate={{
              opacity: [0.7, 1, 0.7],
            }}
            transition={{ repeat: Infinity, duration: 1.8, delay: 0.2 }}
          />
        </div>

        {/* Loading Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Date Loading */}
          <div className="space-y-3">
            <motion.div
              className="h-6 w-3/4 bg-gray-100 rounded"
              animate={{
                opacity: [0.6, 1, 0.6],
              }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            />
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="h-16 bg-gray-50 rounded-lg"
                animate={{
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{
                  repeat: Infinity,
                  duration: 1.5,
                  delay: i * 0.1
                }}
              />
            ))}
          </div>

          {/* Times Loading */}
          <div className="md:col-span-2 space-y-4">
            <motion.div
              className="h-6 w-3/4 bg-gray-100 rounded"
              animate={{
                opacity: [0.6, 1, 0.6],
              }}
              transition={{ repeat: Infinity, duration: 1.5, delay: 0.3 }}
            />
            <div className="grid grid-cols-3 gap-3">
              {[...Array(9)].map((_, i) => (
                <motion.div
                  key={i}
                  className="h-10 bg-gray-50 rounded"
                  animate={{
                    opacity: [0.5, 0.8, 0.5],
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 1.5,
                    delay: i * 0.05
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  

  return (
    <div
      ref={containerRef}
      className="max-w-4xl mx-auto p-4 md:p-6 bg-white rounded-xl shadow-lg"
      style={{
        maxHeight: "80vh",
        overflow: "auto",
      }}
    >
      {/* Header with Back Button */}
      <div className="flex items-center mb-4 md:mb-6 sticky top-0 bg-white pt-2 pb-2 z-10">
        <button
          onClick={onBack}
          className="flex items-center text-green-600 hover:text-green-800 mr-4 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:rounded-md"
        >
          <FaArrowLeft className="mr-2" />
          <span className="hidden sm:inline">Back</span>
        </button>
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">
          Select Appointment
        </h2>
      </div>

      {/* Consultation Guidelines - Collapsible on mobile */}
      <div className="mb-4 md:mb-6 bg-gradient-to-r from-green-50 to-green-50 p-3 md:p-4 rounded-lg border border-gray-200">
        <details className="group">
          <summary className="flex items-center cursor-pointer list-none">
            <FaInfoCircle className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
            <h3 className="font-medium text-gray-800">
              Appointment Guidelines
            </h3>
            <span className="ml-auto transition-transform group-open:rotate-180">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </summary>
          <ul className="mt-2 text-sm text-gray-600 space-y-1.5 pl-7">
            <li className="flex items-start">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 mr-2"></span>
              <span>Select consultation type (Online or Physical)</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2"></span>
              <span>Choose an available date from the calendar</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 mr-2"></span>
              <span>Select your preferred time slot</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-yellow-500 mt-1.5 mr-2"></span>
              <span>Confirm your appointment details</span>
            </li>
          </ul>
        </details>
      </div>

      {/* Consultation Type Tabs */}
      <div className="mb-4 md:mb-6">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => handleTabChange("online")}
            className={`flex items-center justify-center py-2 md:py-3 px-4 md:px-6 font-medium text-sm rounded-t-lg mr-2 transition-colors ${
              activeTab === "online"
                ? "bg-green-50 text-green-700 border-b-2 border-green-500"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            } focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:z-10`}
          >
            <FaLaptopMedical className="mr-1 md:mr-2" />
            <span className="whitespace-nowrap">Online</span>
          </button>
          <button
            onClick={() => handleTabChange("physical")}
            className={`flex items-center justify-center py-2 md:py-3 px-4 md:px-6 font-medium text-sm rounded-t-lg transition-colors ${
              activeTab === "physical"
                ? "bg-green-50 text-green-700 border-b-2 border-green-500"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            } focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:z-10`}
          >
            <FaUserMd className="mr-1 md:mr-2" />
            <span className="whitespace-nowrap">Physical</span>
          </button>
        </div>

        <div className="mt-2 flex items-center text-sm text-gray-600 bg-green-50 p-2 md:p-3 rounded-lg">
          {activeTab === "online" ? (
            <>
              <FaVideo className="text-green-500 mr-2 flex-shrink-0" />
              <span className="text-xs md:text-sm">
               Online consultations will take place via Google Meet. The meeting link will be shared with you through WhatsApp.
              </span>
            </>
          ) : (
            <>
              <FaUserMd className="text-green-500 mr-2 flex-shrink-0" />
              <span className="text-xs md:text-sm">
                Arrive 10 minutes before your scheduled time.
              </span>
            </>
          )}
        </div>
      </div>

      {/* Date and Time Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {/* Date Selection Panel */}
        <div className="md:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 md:p-4 h-full">
            <div className="flex items-center mb-3 md:mb-4">
              <FaCalendarAlt className="mr-2 text-green-500" />
              <h3 className="font-medium text-gray-800">Available Dates</h3>
            </div>

            <div className="space-y-2 max-h-[300px] md:max-h-[400px] overflow-y-auto thin-scrollbar">
              {
              doctorDateTimeError ? 
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-8 bg-yellow-50 rounded-lg"
              >
                <FaCalendarTimes className="mx-auto text-3xl text-yellow-500 mb-3" />
                <h4 className="font-medium text-yellow-800 mb-1">
                  All dates are booked
                </h4>
                <p className="text-sm text-yellow-700">
                  No available dates found for {activeTab === "online" ? "online" : "physical"} consultations
                </p>
              </motion.div>:
              doctorDateTime.map((dateSlot) => (
                <motion.button
                  key={dateSlot.id_doctor_date}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleDateSelect(dateSlot.date)}
                  className={`w-full text-left p-2 md:p-3 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                    selectedDate === dateSlot.date
                      ? "bg-green-100 border border-green-300"
                      : "bg-gray-50 hover:bg-gray-100"
                  }`}
                >
                  <div className="font-medium text-sm md:text-base">
                    {new Date(dateSlot.date).toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                  <div className="text-xs md:text-sm text-gray-500">
                    {dateSlot.timeSlots.filter(
                    (timeSlot) =>
                      timeSlot.status_ == 1 &&
                      timeSlot.count <= 3 &&
                      (timeSlot.patient_type == "0"
                        ? true
                        : isNewPatient
                        ? timeSlot.patient_type == "2"
                        : timeSlot.patient_type == "1")
                  ).length} time slot
                    {dateSlot.timeSlots.filter(
                    (timeSlot) =>
                      timeSlot.status_ == 1 &&
                      timeSlot.count <= 3 &&
                      (timeSlot.patient_type == "0"
                        ? true
                        : isNewPatient
                        ? timeSlot.patient_type == "2"
                        : timeSlot.patient_type == "1")
                  ).length !== 1 ? "s" : ""}
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        {/* Time Selection Panel */}
        <div className="md:col-span-2" ref={timeSlotsRef}>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 md:p-4 h-full">
            <div className="flex items-center mb-3 md:mb-4">
              <FaClock className="mr-2 text-green-500" />
              <h3 className="font-medium text-gray-800">
                {selectedDate
                  ? `Times for ${new Date(selectedDate).toLocaleDateString(
                      "en-US",
                      {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      }
                    )}`
                  : "Select a date to view times"}
              </h3>
            </div>

            {selectedDate ? (
              (() => {
                const dateSlot = doctorDateTime.find(d => d.date === selectedDate);
                const availableSlots = dateSlot?.timeSlots.filter(
                  (timeSlot) =>
                    timeSlot.status_ == 1 &&
                    timeSlot.count <= 3 &&
                    (timeSlot.patient_type == "0"
                      ? true
                      : isNewPatient
                      ? timeSlot.patient_type == "2"
                      : timeSlot.patient_type == "1")
                ) || [];

                return availableSlots.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-8 bg-red-50 rounded-lg"
                  >
                    <FaExclamationTriangle className="mx-auto text-3xl text-red-400 mb-3" />
                    <h4 className="font-medium text-red-800 mb-1">
                      All time slots booked
                    </h4>
                    <p className="text-sm text-red-700">
                      No available times for this date. Please select another date.
                    </p>
                  </motion.div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 md:gap-3">
                    {availableSlots.map((slot) => (
                      <motion.button
                        key={slot.id_doctor_timesloat}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() =>
                          handleTimeSelect(
                            slot.time_sloat,
                            slot.id_doctor_timesloat
                          )
                        }
                        className={`py-2 px-2 md:px-3 text-sm md:text-base rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                          selectedTime === slot.time_sloat
                            ? "bg-green-500 text-white"
                            : "bg-gray-100 hover:bg-gray-200"
                        }`}
                      >
                        {slot.time_sloat}
                      </motion.button>
                    ))}
                  </div>
                );
              })()
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FaClock className="mx-auto text-3xl text-gray-300 mb-2" />
                <p className="text-sm md:text-base">
                  Select a date to view available time slots
                </p>
              </div>
            )}
          </div>

          {/* Confirmation Button */}
          {selectedDate && selectedTime && (
            <motion.div
  initial={{ opacity: 0, y: 10 }}
  animate={{ 
    opacity: 1, 
    y: 0,
    transition: { delay: 0.1 }
  }}
  className="mt-4 md:mt-6 flex justify-end sticky bottom-0 bg-white pt-4 pb-2"
>
  <motion.button
    ref={confirmButtonRef}
    onClick={handleConfirm}
    className="px-4 py-2 md:px-6 md:py-3 bg-green-600 text-white rounded-lg shadow-md hover:opacity-90 transition-opacity flex items-center focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 text-sm md:text-base"
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.98 }}
    animate={{
      scale: [1, 1.02, 1],
      transition: {
        repeat: Infinity,
        repeatType: "reverse",
        duration: 2
      }
    }}
  >
    Confirm {activeTab === "online" ? "Online" : "Physical"} Appointment
    <motion.span
      animate={{
        scale: [1, 1.2, 1],
        transition: {
          repeat: Infinity,
          repeatType: "reverse",
          duration: 2,
          delay: 0.5
        }
      }}
    >
      <FaCheck className="ml-2" />
    </motion.span>
  </motion.button>
</motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DateTimeSelection;
